import { useState, useEffect, useCallback } from 'react';
import { ArrowUpToLine, ChevronsDownUp } from 'lucide-react';
import './JournalFloatingActions.css';

interface JournalFloatingActionsProps {
    /** Number of currently expanded blocks (including todo) */
    expandedCount: number;
    /** Called when collapse all is clicked */
    onCollapseAll: () => void;
    /** Scroll container ref to monitor scroll position */
    scrollContainerRef?: React.RefObject<HTMLDivElement>;
}

export const JournalFloatingActions: React.FC<JournalFloatingActionsProps> = ({
    expandedCount,
    onCollapseAll,
    scrollContainerRef,
}) => {
    const [showBackToTop, setShowBackToTop] = useState(false);
    const [isCollapseHovered, setIsCollapseHovered] = useState(false);
    const showCollapseAll = expandedCount > 0;

    // Monitor scroll position for back to top visibility
    useEffect(() => {
        const container = scrollContainerRef?.current;

        const handleScroll = () => {
            const scrollTop = container?.scrollTop ?? 0;
            setShowBackToTop(scrollTop > 400);
        };

        if (container) {
            container.addEventListener('scroll', handleScroll, { passive: true });
            handleScroll();
        }

        return () => {
            container?.removeEventListener('scroll', handleScroll);
        };
    }, [scrollContainerRef?.current]);

    const handleBackToTop = useCallback(() => {
        const container = scrollContainerRef?.current;
        if (container) {
            container.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }, [scrollContainerRef]);

    return (
        <>
            {/* Collapse All - Top right, expands label on hover */}
            {showCollapseAll && (
                <button
                    className={`floating-action-btn collapse-all-btn ${isCollapseHovered ? 'expanded' : ''}`}
                    onClick={onCollapseAll}
                    onMouseEnter={() => setIsCollapseHovered(true)}
                    onMouseLeave={() => setIsCollapseHovered(false)}
                    aria-label="Collapse all blocks"
                >
                    <span className="collapse-label">Collapse all</span>
                    <ChevronsDownUp className="w-4 h-4" />
                </button>
            )}

            {/* Back to Top - Bottom right */}
            {showBackToTop && (
                <button
                    className="floating-action-btn back-to-top-btn"
                    onClick={handleBackToTop}
                    title="Back to top"
                    aria-label="Scroll back to top"
                >
                    <ArrowUpToLine className="w-4 h-4" />
                </button>
            )}
        </>
    );
};

export default JournalFloatingActions;
